import _ from 'underscore'

import moment from 'moment-timezone'

import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import Table from 'components/Table'
import Spinner from 'components/Spinner'

import EventNameColumnHeader from 'pages/DeviceAggregator/FaceplateMessages/EventNameColumnHeader'

import useDeviceDetails from 'hooks/useDeviceDetails'
import useFaceplateMessages from 'hooks/useFaceplateMessages'

import { longDateTime } from 'helpers/formatters'

type Props = {
  startTime: string
  endTime: string
  deviceId: string
  timeZone: string
}

const noDataMessage = {
  title: 'No Events',
  description: 'No Faceplate Messages Received'
}

const Events = ({ startTime, endTime, deviceId, timeZone }: Props) => {
  const { faceplateMessages, areFaceplateMessagesLoaded } = useFaceplateMessages(deviceId, startTime, endTime)
  const { deviceMetadataDetails, areDeviceDetailsLoaded } = useDeviceDetails(deviceId)
  const [filterEvents, setFilterEvents] = useState<Array<string>>([])

  const uniqueEventNames = (events) => Object.keys(_.groupBy(events || [], event => event.fullyQualifiedName)).sort()

  let deviceTimeZone = timeZone
  if (areDeviceDetailsLoaded) {
    deviceTimeZone ||= String(deviceMetadataDetails?.find((item) => item.column === 'time_zone_name')?.value || '')
  }

  useEffect(() => {
    if (areFaceplateMessagesLoaded) {
      setFilterEvents(uniqueEventNames(faceplateMessages))
    }
  }, [deviceId, areFaceplateMessagesLoaded, faceplateMessages])

  if (!areFaceplateMessagesLoaded) {
    return <Spinner variant="page" />
  }

  return (
    <div>
      <span>{`Faceplate Messages from ${longDateTime(startTime)} to ${longDateTime(endTime)}`}</span>
      <Table items={faceplateMessages || []} {...{ noDataMessage }}>
        <Table.Header>
          <Table.HeaderCell title="Time" />
          <EventNameColumnHeader
            filterOptions={uniqueEventNames(faceplateMessages)}
            selectedFilterOptions={filterEvents}
            onChangeFilterOptionsCallback={setFilterEvents}
          />
          <S.PayloadHeaderCell title="Payload" />
        </Table.Header>
        {_.filter(faceplateMessages || [], event => filterEvents.includes(event.fullyQualifiedName))
          .map((faceplateMessage) => (
            <Table.Row key={faceplateMessage.id}>
              <S.Cell>
                {moment.unix(faceplateMessage.occurredAt).tz(deviceTimeZone).format()}
              </S.Cell>
              <S.Cell>{faceplateMessage.fullyQualifiedName}</S.Cell>
              <S.PayloadCell>
                <pre>{JSON.stringify(faceplateMessage.event, null, 2)}</pre>
              </S.PayloadCell>
            </Table.Row>
          ))}
      </Table>
    </div>
  )
}

export default Events

const S = {
  Cell: styled(Table.Cell)`
    font-weight: bold;
    font-size: 14px;
    flex: 1;
    padding: 20px 0;
  `,
  PayloadHeaderCell: styled(Table.HeaderCell)`
    flex: 2;
  `,
  PayloadCell: styled(Table.Cell)`
    flex: 2;
    font-size: 14px;
    padding: 20px 0;
  `
}
